import { format, parseISO } from 'date-fns'
import z from 'zod'
import type {
  GenerationModifierAward,
  GenerationModifierChainAction,
  GenerationModifierLocationTest,
  GenerationModifierMatchMode,
  GenerationModifierSubject,
  GenerationRuleKind,
  GenerationRuleRounding,
  RestType,
} from './generation-rule-labels'
import type { GenerationModifierNode } from './queries'

export const REST_TYPES: RestType[] = ['weekly', 'reduced_weekly', 'daily']

export interface GenerationModifierMessages {
  nameRequired: string
  noEffect: string
  noCondition: string
  restAwardAmount: string
  perDurationAmount: string
  durationOrder: string
  dateOrder: string
}

export function generationModifierSchema(
  translations: GenerationModifierMessages
) {
  return z
    .object({
      name: z.string().trim().min(1, translations.nameRequired),
      kind: z.enum(['diet', 'salary_day', 'both']),
      appliesTo: z.enum(['work_period', 'rest', 'both']),
      priority: z.number().int(),
      isActive: z.boolean(),
      salaryWorkTime: z.string(),
      users: z.array(z.string()),
      zones: z.array(z.string()),
      countries: z.array(z.string()),
      matchMode: z.enum([
        'starts_in',
        'ends_in',
        'starts_or_ends_in',
        'starts_and_ends_in',
      ]),
      locationTest: z.enum(['inside', 'outside']),
      restTypes: z.array(z.enum(['daily', 'reduced_weekly', 'weekly'])),
      minDurationHours: z.number().min(0).nullable(),
      maxDurationHours: z.number().positive().nullable(),
      validFrom: z.date().nullable(),
      validTo: z.date().nullable(),
      award: z.enum(['none', 'award', 'deny']),
      amount: z.number().min(0).nullable(),
      perDurationHours: z.number().positive().nullable(),
      rounding: z.enum(['floor', 'ceil']),
      maxAmount: z.number().min(0).nullable(),
      salaryCode: z.string(),
      chainAction: z.enum(['none', 'break', 'continue']),
    })
    .superRefine((values, context) => {
      const onRests = values.appliesTo !== 'work_period'
      const issue = (path: string[], message: string) =>
        context.addIssue({ code: 'custom', path, message })

      const hasEffect =
        values.award !== 'none' ||
        values.amount !== null ||
        (values.kind !== 'both' && !!values.salaryCode) ||
        values.chainAction !== 'none'
      if (!hasEffect) issue(['root', 'effects'], translations.noEffect)

      const hasCondition =
        !!values.salaryWorkTime ||
        values.users.length > 0 ||
        values.zones.length > 0 ||
        values.countries.length > 0 ||
        (onRests && values.restTypes.length > 0) ||
        values.minDurationHours !== null ||
        values.maxDurationHours !== null ||
        !!values.validFrom ||
        !!values.validTo
      if (!hasCondition) issue(['root', 'conditions'], translations.noCondition)

      if (onRests && values.award === 'award' && values.amount === null)
        issue(['amount'], translations.restAwardAmount)
      if (values.perDurationHours !== null && values.amount === null)
        issue(['amount'], translations.perDurationAmount)
      if (
        values.minDurationHours !== null &&
        values.maxDurationHours !== null &&
        values.minDurationHours >= values.maxDurationHours
      )
        issue(['maxDurationHours'], translations.durationOrder)
      if (
        values.validFrom &&
        values.validTo &&
        values.validFrom > values.validTo
      )
        issue(['validTo'], translations.dateOrder)
    })
}

export type GenerationModifierForm = z.infer<
  ReturnType<typeof generationModifierSchema>
>

export function defaultValues(
  modifier?: GenerationModifierNode
): GenerationModifierForm {
  return {
    name: modifier?.name ?? '',
    kind: (modifier?.kind as GenerationRuleKind) ?? 'diet',
    appliesTo:
      (modifier?.appliesTo as GenerationModifierSubject) ?? 'work_period',
    priority: modifier?.priority ?? 100,
    isActive: modifier?.isActive ?? true,
    salaryWorkTime: modifier?.salaryWorkTime?.id ?? '',
    users: modifier?.users.map(user => user.id) ?? [],
    zones: modifier?.zones.map(zone => zone.id) ?? [],
    countries: modifier?.countries.map(country => country.id) ?? [],
    matchMode:
      (modifier?.matchMode as GenerationModifierMatchMode) ?? 'ends_in',
    locationTest:
      (modifier?.locationTest as GenerationModifierLocationTest) ?? 'inside',
    restTypes: (modifier?.restTypes as RestType[]) ?? [],
    minDurationHours: modifier?.minDurationHours ?? null,
    maxDurationHours: modifier?.maxDurationHours ?? null,
    validFrom: modifier?.validFrom ? parseISO(modifier.validFrom) : null,
    validTo: modifier?.validTo ? parseISO(modifier.validTo) : null,
    award: (modifier?.award as GenerationModifierAward) ?? 'none',
    amount: modifier?.amount ?? null,
    perDurationHours: modifier?.perDurationHours ?? null,
    rounding: (modifier?.rounding as GenerationRuleRounding) ?? 'ceil',
    maxAmount: modifier?.maxAmount ?? null,
    salaryCode: modifier?.salaryCode?.id ?? '',
    chainAction:
      (modifier?.chainAction as GenerationModifierChainAction) ?? 'none',
  }
}

/** The mutation input. Values that no longer apply -- rest types on a
 * work-period modifier, a salary code on a BOTH one -- are sent cleared, so a
 * patch does not keep them. */
export function toInput(values: GenerationModifierForm) {
  const onRests = values.appliesTo !== 'work_period'
  const date = (value: Date | null) =>
    value ? format(value, 'yyyy-MM-dd') : null

  return {
    name: values.name.trim(),
    kind: values.kind,
    appliesTo: values.appliesTo,
    priority: values.priority,
    isActive: values.isActive,
    salaryWorkTime: values.salaryWorkTime || null,
    users: values.users,
    zones: values.zones,
    countries: values.countries,
    matchMode: values.matchMode,
    locationTest: values.locationTest,
    restTypes: onRests ? values.restTypes : [],
    minDurationHours: values.minDurationHours,
    maxDurationHours: values.maxDurationHours,
    validFrom: date(values.validFrom),
    validTo: date(values.validTo),
    award: values.award === 'none' ? null : values.award,
    amount: values.amount,
    perDurationHours: values.amount === null ? null : values.perDurationHours,
    rounding: values.rounding,
    maxAmount: values.amount === null ? null : values.maxAmount,
    salaryCode: values.kind === 'both' ? null : values.salaryCode || null,
    chainAction:
      values.chainAction === 'none' ||
      (values.chainAction === 'continue' && !onRests)
        ? null
        : values.chainAction,
  }
}
